import { useState } from "react";
import { cn } from "../../lib/utils.ts";
import type { Playbook, Platform } from "../../../../shared/types.ts";
import { Button } from "../ui/Button.tsx";

interface PlaybookCardProps {
  playbook: Playbook;
  onActivate: (playbookId: string) => void;
  isActivating?: boolean;
}

const platformColors: Record<Platform, string> = {
  facebook: "bg-blue-100 text-blue-700",
  linkedin: "bg-blue-100 text-blue-700",
  twitter: "bg-sky-100 text-sky-700",
  instagram: "bg-pink-100 text-pink-700",
  threads: "bg-gray-100 text-gray-700",
  shopify: "bg-emerald-100 text-emerald-700",
  tiktok: "bg-fuchsia-100 text-fuchsia-700",
  youtube: "bg-red-100 text-red-700",
  bluesky: "bg-sky-100 text-sky-700",
};

function getPlatformBadges(playbook: Playbook): Platform[] {
  const platforms = new Set<Platform>();
  for (const step of playbook.steps) {
    for (const p of step.platforms) {
      platforms.add(p);
    }
  }
  return Array.from(platforms);
}

export function PlaybookCard({
  playbook,
  onActivate,
  isActivating = false,
}: PlaybookCardProps) {
  const [expanded, setExpanded] = useState(false);
  const platforms = getPlatformBadges(playbook);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      {/* Header */}
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-slate-900">
          {playbook.name}
        </h3>
        <p className="mt-1 text-sm text-slate-500">{playbook.description}</p>
      </div>

      {/* Meta info */}
      <div className="mb-4 flex items-center gap-3">
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
          {playbook.steps.length} day{playbook.steps.length !== 1 ? "s" : ""}
        </span>
        <div className="flex flex-wrap gap-1">
          {platforms.map((p) => (
            <span
              key={p}
              className={cn(
                "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                platformColors[p],
              )}
            >
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* Expandable steps */}
      <button
        type="button"
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
        onClick={() => setExpanded(!expanded)}
      >
        <svg
          className={cn(
            "h-4 w-4 transition-transform",
            expanded && "rotate-90",
          )}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 4.5l7.5 7.5-7.5 7.5"
          />
        </svg>
        {expanded ? "Hide steps" : "View steps"}
      </button>

      {expanded && (
        <div className="mb-4 space-y-2">
          {playbook.steps.map((step) => (
            <div
              key={step.day}
              className="rounded-lg border border-slate-100 bg-slate-50 p-3"
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                  {step.day}
                </span>
                <span className="text-xs text-slate-400">
                  Day {step.day}
                </span>
              </div>
              <p className="text-sm text-slate-600">{step.prompt_template}</p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {step.platforms.map((p) => (
                  <span
                    key={p}
                    className={cn(
                      "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium capitalize",
                      platformColors[p],
                    )}
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activate button */}
      <Button
        variant="primary"
        size="md"
        loading={isActivating}
        disabled={isActivating}
        onClick={() => onActivate(playbook.id)}
        className="w-full"
      >
        Activate Playbook
      </Button>
    </div>
  );
}
